/**
 * React Query hooks for admin data.
 *
 * Overview, Users, Support, Restore, Storage, Audio, Admin & Audit, and the
 * live-backed second-wave admin slices hit the main-Worker `/api/admin/*`
 * routes. Remaining unsupported surfaces still use fixtures until those
 * endpoints land.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-27-v3
 */
import { useQuery } from '@tanstack/react-query';
import {
    FIXTURE_AI_KPIS,
    FIXTURE_AI_MODEL_USAGE,
    FIXTURE_AI_TOP_USERS,
    FIXTURE_BYOK_HEALTH,
    FIXTURE_CHANGELOG,
    FIXTURE_CHANGELOG_KPIS,
    FIXTURE_DISPUTES,
    FIXTURE_FLAGS,
    FIXTURE_FLAG_KPIS,
    FIXTURE_IMPORT_KPIS,
    FIXTURE_IMPORT_PATTERNS,
    FIXTURE_IMPORT_PROMPTS,
    FIXTURE_IMPORT_RUNS,
    FIXTURE_JOBS,
    FIXTURE_JOB_FAILURES,
    FIXTURE_JOB_KPIS,
    FIXTURE_LICENSES,
    FIXTURE_LICENSE_TYPES,
    FIXTURE_LICENSING_KPIS,
    FIXTURE_PAYOUT_STATUS,
    FIXTURE_PROMPT_REGISTRY,
    FIXTURE_PUBLISHERS,
    FIXTURE_ROUTING_POLICY,
    FIXTURE_SHARES,
    FIXTURE_SHARE_KPIS,
    FIXTURE_SHARE_REPORTS
} from './fixtures';
import { apiDelete, apiDownloadText, apiGet, apiPatch, apiPost } from './http';
import type {
    AcceptanceLogEntry,
    ActivityEvent,
    AdminAuditEntry,
    AdminAuditKpis,
    AdminRole,
    AdminUser,
    AdminUserRow,
    AiKpis,
    AiModelUsage,
    AiTopUser,
    Alert,
    AnalyticsEvent,
    AnalyticsKpis,
    AudioAccuracy,
    AudioCampaign,
    AudioFailureRow,
    AudioFormatRow,
    AudioKpis,
    BackgroundJob,
    BillingHealthRow,
    BillingKpis,
    BillingTransaction,
    BroadcastRow,
    ByokHealth,
    CannedReply,
    ChangelogEntry,
    ChangelogKpis,
    ChannelDelivery,
    CohortRow,
    ComplianceKpis,
    DauPoint,
    DbPoolDetail,
    DropboxTokenExportRow,
    DropboxTokenHealth,
    ElectronVersionBucket,
    ErrorLogRow,
    FeatureAdoption,
    FeatureFlagKpis,
    FeatureFlagRow,
    FunnelStep,
    GdprExportRow,
    GeoRow,
    HealthIntegration,
    HeatmapCell,
    HelpDocQuery,
    HelpDocRow,
    HelpDocsKpis,
    ImpersonationLogEntry,
    ImportKpis,
    ImportPattern,
    ImportPrompt,
    ImportRun,
    JobFailure,
    JobKpis,
    Kpi,
    KvNamespace,
    LegalDocSummary,
    LegalDraftResult,
    LegalVersionDiff,
    LegalVersionRow,
    LicenseDispute,
    LicenseRow,
    LicenseType,
    LicensingKpis,
    NotificationFailure,
    NotificationKpis,
    PayoutStatusRow,
    PlanDistributionRow,
    PlanProfitRow,
    PlatformSplit,
    PromptRegistryRow,
    Publisher,
    PulseService,
    R2Bucket,
    RestorableAction,
    RestoreActionResult,
    RestoreKpis,
    RestoreTimelineRow,
    RevenueSummary,
    RoutingPolicy,
    SegmentCounts,
    ServiceCost,
    SessionPolicy,
    ShareKpis,
    ShareReport,
    ShareRow,
    SignupPoint,
    SoftDeleteRow,
    StorageKpis,
    StorageOrphanSummary,
    StoragePurgeResult,
    StorageRefreshResult,
    SupportKpis,
    SupportTicket,
    SupportTicketPatch,
    TaxonomyCategory,
    TaxonomyCategoryInput,
    TaxonomyKpis,
    TaxonomyStage,
    TaxonomyTag,
    TaxonomyTagInput,
    TaxonomyWorkflow,
    TemplateImportResponse,
    TemplatePerformance,
    TicketMessage,
    TopErrorRow,
    TopStorageUser,
    UserAuditEntry,
    UserBillingHealth,
    UserDevice,
    UserLegalAcceptance,
    UserSegment,
    WorkerRateSnapshot
} from './types';
import type { AuditQueryFilters } from './types';
import { buildAuditQueryString } from './audit';
import { normalizeTicketPatch } from './support-transcript';

type RawRestorableAction = RestorableAction & Record<string, unknown>;

type RestorableActionsApiResponse =
    | RawRestorableAction[]
    | {
          actions?: RawRestorableAction[];
          data?: RawRestorableAction[];
          total?: number;
          has_more?: boolean;
      };

/** Simulate network latency for realistic loading states on fixture-backed hooks. */
function simulate<T>(data: T, ms = 80): Promise<T> {
    return new Promise((resolve) => setTimeout(() => resolve(data), ms));
}

function withDays(path: string, days?: number) {
    if (!days) return path;
    const joiner = path.includes('?') ? '&' : '?';
    return `${path}${joiner}days=${encodeURIComponent(String(days))}`;
}

// ------- Overview (live) --------------------------------------

export function useKpis(days = 30) {
    return useQuery<Kpi[]>({
        queryKey: ['admin', 'overview', 'kpis', days],
        queryFn: () => apiGet<Kpi[]>(withDays('/api/admin/overview/kpis', days)),
        refetchInterval: 10_000
    });
}

export function useAlerts() {
    return useQuery<Alert[]>({
        queryKey: ['admin', 'overview', 'alerts'],
        queryFn: () => apiGet<Alert[]>('/api/admin/overview/alerts'),
        refetchInterval: 30_000
    });
}

export function useActivity() {
    return useQuery<ActivityEvent[]>({
        queryKey: ['admin', 'overview', 'activity'],
        queryFn: () => apiGet<ActivityEvent[]>('/api/admin/overview/activity'),
        refetchInterval: 10_000
    });
}

export function usePulse() {
    return useQuery<PulseService[]>({
        queryKey: ['admin', 'overview', 'pulse'],
        queryFn: () => apiGet<PulseService[]>('/api/admin/overview/pulse'),
        refetchInterval: 30_000
    });
}

export function useCharts(days = 30) {
    return useQuery<{
        signups: SignupPoint[];
        revenue: RevenueSummary;
        top_errors: TopErrorRow[];
    }>({
        queryKey: ['admin', 'overview', 'charts', days],
        queryFn: () =>
            apiGet<{
                signups: SignupPoint[];
                revenue: RevenueSummary;
                top_errors: TopErrorRow[];
            }>(withDays('/api/admin/overview/charts', days))
    });
}

// ------- Users --------------------------------------

export function useUserSegmentCounts() {
    return useQuery<SegmentCounts>({
        queryKey: ['admin', 'users', 'segments'],
        queryFn: () => apiGet<SegmentCounts>('/api/admin/users/segments'),
        refetchInterval: 60_000
    });
}

export interface UserListFilters {
    segment: UserSegment;
    search: string;
    plan: string;
    appVersion: string;
    sort: string;
}

function buildUserListQuery(filters: UserListFilters): string {
    const p = new URLSearchParams();
    if (filters.segment !== 'all') p.set('segment', filters.segment);
    if (filters.search.trim()) p.set('q', filters.search.trim());
    if (filters.plan !== 'all') p.set('plan', filters.plan);
    if (filters.appVersion !== 'all') p.set('v', filters.appVersion);
    if (filters.sort) p.set('sort', filters.sort);
    const qs = p.toString();
    return qs ? `?${qs}` : '';
}

export function useUsers(filters: UserListFilters) {
    return useQuery<AdminUser[]>({
        queryKey: ['admin', 'users', 'list', filters],
        queryFn: () => apiGet<AdminUser[]>(`/api/admin/users${buildUserListQuery(filters)}`)
    });
}

export function useUser(id: string | null) {
    return useQuery<AdminUser | null>({
        queryKey: ['admin', 'users', 'detail', id],
        queryFn: () => apiGet<AdminUser | null>(`/api/admin/users/${id}`),
        enabled: !!id
    });
}

export function useUserBillingHealth(id: string | null) {
    return useQuery<UserBillingHealth>({
        queryKey: ['admin', 'users', 'billing', id],
        queryFn: () => apiGet<UserBillingHealth>(`/api/admin/users/${id}/billing-health`),
        enabled: !!id
    });
}

export function useUserLegalAcceptance(id: string | null) {
    return useQuery<UserLegalAcceptance[]>({
        queryKey: ['admin', 'users', 'legal', id],
        queryFn: () => apiGet<UserLegalAcceptance[]>(`/api/admin/users/${id}/legal-acceptance`),
        enabled: !!id
    });
}

export function useUserAuditLog(id: string | null) {
    return useQuery<UserAuditEntry[]>({
        queryKey: ['admin', 'users', 'audit', id],
        queryFn: () => apiGet<UserAuditEntry[]>(`/api/admin/users/${id}/audit`),
        enabled: !!id
    });
}

export function useUserDevices(id: string | null) {
    return useQuery<UserDevice[]>({
        queryKey: ['admin', 'users', 'devices', id],
        queryFn: () => apiGet<UserDevice[]>(`/api/admin/users/${id}/devices`),
        enabled: !!id
    });
}

// ------- Support --------------------------------------

export function useSupportKpis() {
    return useQuery<SupportKpis>({
        queryKey: ['admin', 'support', 'kpis'],
        queryFn: () => apiGet<SupportKpis>('/api/admin/support/kpis'),
        refetchInterval: 30_000
    });
}

export function useSupportTickets() {
    return useQuery<SupportTicket[]>({
        queryKey: ['admin', 'support', 'tickets'],
        queryFn: () => apiGet<SupportTicket[]>('/api/admin/support/tickets'),
        refetchInterval: 30_000
    });
}

export function useTicketMessages(ticketId: string | null) {
    return useQuery<TicketMessage[]>({
        queryKey: ['admin', 'support', 'messages', ticketId],
        queryFn: () => apiGet<TicketMessage[]>(`/api/admin/support/tickets/${ticketId}/messages`),
        enabled: !!ticketId
    });
}

export function updateSupportTicket(id: string, patch: SupportTicketPatch) {
    return apiPatch<SupportTicket>(`/api/admin/support/tickets/${id}`, normalizeTicketPatch(patch));
}

export function useCannedReplies() {
    return useQuery<CannedReply[]>({
        queryKey: ['admin', 'support', 'canned-replies'],
        queryFn: () => apiGet<CannedReply[]>('/api/admin/support/canned-replies')
    });
}

// ------- System Health --------------------------------------

export function useHealthIntegrations() {
    return useQuery<HealthIntegration[]>({
        queryKey: ['admin', 'health', 'integrations'],
        queryFn: () => apiGet<HealthIntegration[]>('/api/admin/health/integrations'),
        refetchInterval: 30_000
    });
}

export function useDbPool() {
    return useQuery<DbPoolDetail>({
        queryKey: ['admin', 'health', 'db-pool'],
        queryFn: () => apiGet<DbPoolDetail>('/api/admin/health/db-pool'),
        refetchInterval: 30_000
    });
}

export function useWorkerRate() {
    return useQuery<WorkerRateSnapshot>({
        queryKey: ['admin', 'health', 'worker-rate'],
        queryFn: () => apiGet<WorkerRateSnapshot>('/api/admin/health/worker-rate')
    });
}

// ------- Background Jobs --------------------------------------

export function useJobKpis() {
    return useQuery<JobKpis>({
        queryKey: ['admin', 'jobs', 'kpis'],
        queryFn: () => simulate(FIXTURE_JOB_KPIS)
    });
}

export function useJobs() {
    return useQuery<BackgroundJob[]>({
        queryKey: ['admin', 'jobs', 'list'],
        queryFn: () => simulate(FIXTURE_JOBS)
    });
}

export function useJobFailures() {
    return useQuery<JobFailure[]>({
        queryKey: ['admin', 'jobs', 'failures'],
        queryFn: () => simulate(FIXTURE_JOB_FAILURES)
    });
}

// ------- Error Logs --------------------------------------

export function useErrorLogs() {
    return useQuery<ErrorLogRow[]>({
        queryKey: ['admin', 'errors', 'list'],
        queryFn: () => apiGet<ErrorLogRow[]>('/api/admin/errors'),
        refetchInterval: 30_000
    });
}

// ------- Billing --------------------------------------

export function useBillingKpis() {
    return useQuery<BillingKpis>({
        queryKey: ['admin', 'billing', 'kpis'],
        queryFn: () => apiGet<BillingKpis>('/api/admin/billing/kpis')
    });
}

export function usePlanProfit() {
    return useQuery<PlanProfitRow[]>({
        queryKey: ['admin', 'billing', 'plan-profit'],
        queryFn: () => apiGet<PlanProfitRow[]>('/api/admin/billing/plan-profit')
    });
}

export function useServiceCosts() {
    return useQuery<ServiceCost[]>({
        queryKey: ['admin', 'billing', 'cost-breakdown'],
        queryFn: () => apiGet<ServiceCost[]>('/api/admin/billing/cost-breakdown')
    });
}

export function usePlanDistribution() {
    return useQuery<PlanDistributionRow[]>({
        queryKey: ['admin', 'billing', 'plan-distribution'],
        queryFn: () => apiGet<PlanDistributionRow[]>('/api/admin/billing/plan-distribution')
    });
}

export function useBillingTransactions() {
    return useQuery<BillingTransaction[]>({
        queryKey: ['admin', 'billing', 'transactions'],
        queryFn: () => apiGet<BillingTransaction[]>('/api/admin/billing/transactions')
    });
}

export function useBillingHealthQueue() {
    return useQuery<BillingHealthRow[]>({
        queryKey: ['admin', 'billing', 'health-queue'],
        queryFn: () => apiGet<BillingHealthRow[]>('/api/admin/billing/health-queue')
    });
}

// ------- Licensing --------------------------------------

export function useLicensingKpis() {
    return useQuery<LicensingKpis>({
        queryKey: ['admin', 'licensing', 'kpis'],
        queryFn: () => simulate(FIXTURE_LICENSING_KPIS)
    });
}

export function useLicenseTypes() {
    return useQuery<LicenseType[]>({
        queryKey: ['admin', 'licensing', 'types'],
        queryFn: () => simulate(FIXTURE_LICENSE_TYPES)
    });
}

export function useLicenses() {
    return useQuery<LicenseRow[]>({
        queryKey: ['admin', 'licensing', 'licenses'],
        queryFn: () => simulate(FIXTURE_LICENSES)
    });
}

export function usePayoutStatus() {
    return useQuery<PayoutStatusRow[]>({
        queryKey: ['admin', 'licensing', 'payout-status'],
        queryFn: () => simulate(FIXTURE_PAYOUT_STATUS)
    });
}

export function useDisputes() {
    return useQuery<LicenseDispute[]>({
        queryKey: ['admin', 'licensing', 'disputes'],
        queryFn: () => simulate(FIXTURE_DISPUTES)
    });
}

export function usePublishers() {
    return useQuery<Publisher[]>({
        queryKey: ['admin', 'licensing', 'publishers'],
        queryFn: () => simulate(FIXTURE_PUBLISHERS)
    });
}

// ------- Analytics --------------------------------------

export function useAnalyticsKpis(days = 30) {
    return useQuery<AnalyticsKpis>({
        queryKey: ['admin', 'analytics', 'kpis', days],
        queryFn: () => apiGet<AnalyticsKpis>(withDays('/api/admin/analytics/kpis', days))
    });
}

export function useDauChart(days = 30) {
    return useQuery<DauPoint[]>({
        queryKey: ['admin', 'analytics', 'dau', days],
        queryFn: () => apiGet<DauPoint[]>(withDays('/api/admin/analytics/dau', days))
    });
}

export function usePlatformSplit(days = 30) {
    return useQuery<PlatformSplit[]>({
        queryKey: ['admin', 'analytics', 'platform', days],
        queryFn: () => apiGet<PlatformSplit[]>(withDays('/api/admin/analytics/platform', days))
    });
}

export function useHeatmap(days = 30) {
    return useQuery<HeatmapCell[]>({
        queryKey: ['admin', 'analytics', 'heatmap', days],
        queryFn: () => apiGet<HeatmapCell[]>(withDays('/api/admin/analytics/heatmap', days))
    });
}

export function useFeatureAdoption(days = 30) {
    return useQuery<FeatureAdoption[]>({
        queryKey: ['admin', 'analytics', 'adoption', days],
        queryFn: () => apiGet<FeatureAdoption[]>(withDays('/api/admin/analytics/adoption', days))
    });
}

export function useGeography(days = 30) {
    return useQuery<GeoRow[]>({
        queryKey: ['admin', 'analytics', 'geography', days],
        queryFn: () => apiGet<GeoRow[]>(withDays('/api/admin/analytics/geography', days))
    });
}

export function useAnalyticsEvents() {
    return useQuery<AnalyticsEvent[]>({
        queryKey: ['admin', 'analytics', 'events'],
        queryFn: () => apiGet<AnalyticsEvent[]>('/api/admin/analytics/events')
    });
}

export function useFunnel(name: 'onboarding' | 'activation') {
    return useQuery<FunnelStep[]>({
        queryKey: ['admin', 'analytics', 'funnels', name],
        queryFn: () => apiGet<FunnelStep[]>(`/api/admin/analytics/funnels/${name}`)
    });
}

export function useCohorts() {
    return useQuery<CohortRow[]>({
        queryKey: ['admin', 'analytics', 'cohorts'],
        queryFn: () => apiGet<CohortRow[]>('/api/admin/analytics/cohorts')
    });
}

// ------- Compliance --------------------------------------

export function useComplianceKpis() {
    return useQuery<ComplianceKpis>({
        queryKey: ['admin', 'compliance', 'kpis'],
        queryFn: () => apiGet<ComplianceKpis>('/api/admin/compliance/acceptance')
    });
}

export function useLegalDocs() {
    return useQuery<LegalDocSummary[]>({
        queryKey: ['admin', 'compliance', 'documents'],
        queryFn: () => apiGet<LegalDocSummary[]>('/api/admin/compliance/documents')
    });
}

export function useLegalVersions(slug: 'tos' | 'privacy') {
    return useQuery<LegalVersionRow[]>({
        queryKey: ['admin', 'compliance', 'versions', slug],
        queryFn: () => apiGet<LegalVersionRow[]>(`/api/admin/compliance/documents/${slug}/versions`)
    });
}

export function useSessionPolicy() {
    return useQuery<SessionPolicy>({
        queryKey: ['admin', 'compliance', 'session-policy'],
        queryFn: () => apiGet<SessionPolicy>('/api/admin/compliance/session-policy')
    });
}

export function useAcceptanceLog() {
    return useQuery<AcceptanceLogEntry[]>({
        queryKey: ['admin', 'compliance', 'acceptance-log'],
        queryFn: () => apiGet<AcceptanceLogEntry[]>('/api/admin/compliance/acceptance-log')
    });
}

export interface PublishLegalVersionInput {
    version: string;
    body_markdown: string;
    summary_of_change: string;
    effective_at: string;
    enforcement_mode: LegalVersionRow['enforcement_mode'];
    grace_period_days: number;
    reason?: string;
}

export interface GenerateLegalDraftInput {
    version?: string;
    brief?: string;
    jurisdiction?: string;
    audience?: string;
}

export async function generateLegalDraft(slug: 'tos' | 'privacy', input: GenerateLegalDraftInput) {
    return apiPost<{ success: true; model: string; draft: LegalDraftResult }>(
        `/api/admin/compliance/documents/${slug}/generate-draft`,
        input
    );
}

export async function publishLegalVersion(
    slug: 'tos' | 'privacy',
    input: PublishLegalVersionInput
) {
    return apiPost<{ success: true; version: LegalVersionRow }>(
        `/api/admin/compliance/documents/${slug}/versions`,
        input
    );
}

export async function getLegalVersionDiff(slug: 'tos' | 'privacy', id: string) {
    return apiGet<LegalVersionDiff>(`/api/admin/compliance/documents/${slug}/versions/${id}/diff`);
}

export async function rollbackLegalVersion(slug: 'tos' | 'privacy', id: string, reason: string) {
    return apiPost<{ success: true; version: LegalVersionRow }>(
        `/api/admin/compliance/documents/${slug}/versions/${id}/rollback`,
        { reason }
    );
}

export async function resendLegalNotifications(
    slug: 'tos' | 'privacy',
    id: string,
    reason: string
) {
    return apiPost<{ success: true; broadcast_id: string | null; pending_count: number }>(
        `/api/admin/compliance/documents/${slug}/versions/${id}/resend-notifications`,
        { reason }
    );
}

// ------- Feature Flags --------------------------------------

export function useFlagKpis() {
    return useQuery<FeatureFlagKpis>({
        queryKey: ['admin', 'flags', 'kpis'],
        queryFn: () => simulate(FIXTURE_FLAG_KPIS)
    });
}

export function useFlags() {
    return useQuery<FeatureFlagRow[]>({
        queryKey: ['admin', 'flags', 'list'],
        queryFn: () => simulate(FIXTURE_FLAGS)
    });
}

// ------- AI & BYOK --------------------------------------

export function useAiKpis() {
    return useQuery<AiKpis>({
        queryKey: ['admin', 'ai', 'kpis'],
        queryFn: () => simulate(FIXTURE_AI_KPIS)
    });
}

export function useAiModelUsage() {
    return useQuery<AiModelUsage[]>({
        queryKey: ['admin', 'ai', 'usage'],
        queryFn: () => simulate(FIXTURE_AI_MODEL_USAGE)
    });
}

export function useByokHealth() {
    return useQuery<ByokHealth>({
        queryKey: ['admin', 'ai', 'byok-health'],
        queryFn: () => simulate(FIXTURE_BYOK_HEALTH)
    });
}

export function useRoutingPolicy() {
    return useQuery<RoutingPolicy>({
        queryKey: ['admin', 'ai', 'routing'],
        queryFn: () => simulate(FIXTURE_ROUTING_POLICY)
    });
}

export function useAiTopUsers() {
    return useQuery<AiTopUser[]>({
        queryKey: ['admin', 'ai', 'top-users'],
        queryFn: () => simulate(FIXTURE_AI_TOP_USERS)
    });
}

export function usePromptRegistry() {
    return useQuery<PromptRegistryRow[]>({
        queryKey: ['admin', 'ai', 'prompts'],
        queryFn: () => simulate(FIXTURE_PROMPT_REGISTRY)
    });
}

// ------- Taxonomy --------------------------------------

export function useTaxonomyKpis() {
    return useQuery<TaxonomyKpis>({
        queryKey: ['admin', 'taxonomy', 'kpis'],
        queryFn: () => apiGet<TaxonomyKpis>('/api/admin/taxonomy/kpis')
    });
}

export function useStages() {
    return useQuery<TaxonomyStage[]>({
        queryKey: ['admin', 'taxonomy', 'stages'],
        queryFn: () => apiGet<TaxonomyStage[]>('/api/admin/taxonomy/stages')
    });
}

export function useWorkflows() {
    return useQuery<TaxonomyWorkflow[]>({
        queryKey: ['admin', 'taxonomy', 'workflows'],
        queryFn: () => apiGet<TaxonomyWorkflow[]>('/api/admin/taxonomy/workflows')
    });
}

export function useTags() {
    return useQuery<TaxonomyTag[]>({
        queryKey: ['admin', 'taxonomy', 'tags'],
        queryFn: () => apiGet<TaxonomyTag[]>('/api/admin/taxonomy/tags')
    });
}

export function useTagCategories() {
    return useQuery<TaxonomyCategory[]>({
        queryKey: ['admin', 'taxonomy', 'categories'],
        queryFn: () => apiGet<TaxonomyCategory[]>('/api/admin/taxonomy/categories')
    });
}

// ------- Taxonomy CRUD (TAX_SPEC §5) ----------------------

export function createTaxonomyTag(input: TaxonomyTagInput) {
    return apiPost<TaxonomyTag>('/api/admin/taxonomy/tags', input);
}

export function updateTaxonomyTag(id: string, input: Partial<TaxonomyTagInput>) {
    return apiPatch<TaxonomyTag>(`/api/admin/taxonomy/tags/${id}`, input);
}

export function deleteTaxonomyTag(id: string) {
    return apiDelete<{ success: boolean }>(`/api/admin/taxonomy/tags/${id}`);
}

export function createTaxonomyCategory(input: TaxonomyCategoryInput) {
    return apiPost<TaxonomyCategory>('/api/admin/taxonomy/categories', input);
}

export function updateTaxonomyCategory(id: string, input: Partial<TaxonomyCategoryInput>) {
    return apiPatch<TaxonomyCategory>(`/api/admin/taxonomy/categories/${id}`, input);
}

export function deleteTaxonomyCategory(id: string) {
    return apiDelete<{ success: boolean }>(`/api/admin/taxonomy/categories/${id}`);
}

export function reorderTaxonomyTags(tagIds: string[]) {
    return apiPatch<{ success: boolean; count: number }>('/api/admin/taxonomy/tags/reorder', {
        tag_ids: tagIds
    });
}

export function reorderTaxonomyCategories(categoryIds: string[]) {
    return apiPatch<{ success: boolean; count: number }>('/api/admin/taxonomy/categories/reorder', {
        category_ids: categoryIds
    });
}

// ------- Share Pages --------------------------------------

export function useShareKpis() {
    return useQuery<ShareKpis>({
        queryKey: ['admin', 'shares', 'kpis'],
        queryFn: () => simulate(FIXTURE_SHARE_KPIS)
    });
}

export function useShares() {
    return useQuery<ShareRow[]>({
        queryKey: ['admin', 'shares', 'list'],
        queryFn: () => simulate(FIXTURE_SHARES)
    });
}

export function useShareReports() {
    return useQuery<ShareReport[]>({
        queryKey: ['admin', 'shares', 'reports'],
        queryFn: () => simulate(FIXTURE_SHARE_REPORTS)
    });
}

// ------- Help Docs --------------------------------------

export function useHelpDocsKpis() {
    return useQuery<HelpDocsKpis>({
        queryKey: ['admin', 'help-docs', 'kpis'],
        queryFn: () => apiGet<HelpDocsKpis>('/api/admin/help-docs/kpis')
    });
}

export function useHelpDocs() {
    return useQuery<HelpDocRow[]>({
        queryKey: ['admin', 'help-docs', 'list'],
        queryFn: () => apiGet<HelpDocRow[]>('/api/admin/help-docs/list')
    });
}

export function useTopQueries() {
    return useQuery<HelpDocQuery[]>({
        queryKey: ['admin', 'help-docs', 'top-queries'],
        queryFn: () => apiGet<HelpDocQuery[]>('/api/admin/help-docs/top-queries')
    });
}

export function useFailedQueries() {
    return useQuery<HelpDocQuery[]>({
        queryKey: ['admin', 'help-docs', 'failed-queries'],
        queryFn: () => apiGet<HelpDocQuery[]>('/api/admin/help-docs/failed-queries')
    });
}

// ------- Changelog --------------------------------------

export function useChangelogKpis() {
    return useQuery<ChangelogKpis>({
        queryKey: ['admin', 'changelog', 'kpis'],
        queryFn: () => simulate(FIXTURE_CHANGELOG_KPIS)
    });
}

export function useChangelog() {
    return useQuery<ChangelogEntry[]>({
        queryKey: ['admin', 'changelog', 'list'],
        queryFn: () => simulate(FIXTURE_CHANGELOG)
    });
}

// ------- Import Pipeline --------------------------------------

export function useImportKpis() {
    return useQuery<ImportKpis>({
        queryKey: ['admin', 'imports', 'kpis'],
        queryFn: () => simulate(FIXTURE_IMPORT_KPIS)
    });
}

export function useImportRuns() {
    return useQuery<ImportRun[]>({
        queryKey: ['admin', 'imports', 'runs'],
        queryFn: () => simulate(FIXTURE_IMPORT_RUNS)
    });
}

export function useImportPrompts() {
    return useQuery<ImportPrompt[]>({
        queryKey: ['admin', 'imports', 'prompts'],
        queryFn: () => simulate(FIXTURE_IMPORT_PROMPTS)
    });
}

export function useImportPatterns() {
    return useQuery<ImportPattern[]>({
        queryKey: ['admin', 'imports', 'patterns'],
        queryFn: () => simulate(FIXTURE_IMPORT_PATTERNS)
    });
}

// ------- Storage --------------------------------------

export function useStorageKpis() {
    return useQuery<StorageKpis>({
        queryKey: ['admin', 'storage', 'kpis'],
        queryFn: () => apiGet<StorageKpis>('/api/admin/storage/kpis'),
        refetchInterval: 60_000
    });
}

export function useR2Buckets() {
    return useQuery<R2Bucket[]>({
        queryKey: ['admin', 'storage', 'r2'],
        queryFn: () => apiGet<R2Bucket[]>('/api/admin/storage/r2'),
        refetchInterval: 60_000
    });
}

export function useKvNamespaces() {
    return useQuery<KvNamespace[]>({
        queryKey: ['admin', 'storage', 'kv'],
        queryFn: () => apiGet<KvNamespace[]>('/api/admin/storage/kv'),
        refetchInterval: 60_000
    });
}

export function useDropboxTokenHealth() {
    return useQuery<DropboxTokenHealth>({
        queryKey: ['admin', 'storage', 'dropbox'],
        queryFn: () => apiGet<DropboxTokenHealth>('/api/admin/storage/dropbox-tokens'),
        refetchInterval: 30_000
    });
}

export function useTopStorageUsers() {
    return useQuery<TopStorageUser[]>({
        queryKey: ['admin', 'storage', 'top-users'],
        queryFn: () => apiGet<TopStorageUser[]>('/api/admin/storage/top-users'),
        refetchInterval: 60_000
    });
}

export function useStorageOrphans() {
    return useQuery<StorageOrphanSummary>({
        queryKey: ['admin', 'storage', 'orphans'],
        queryFn: () => apiGet<StorageOrphanSummary>('/api/admin/storage/orphans'),
        refetchInterval: 60_000
    });
}

export async function refreshExpiredDropboxTokens() {
    return apiPost<StorageRefreshResult>('/api/admin/storage/dropbox-tokens/refresh-expired');
}

export async function exportDropboxTokenList() {
    return apiGet<DropboxTokenExportRow[]>('/api/admin/storage/dropbox-tokens/export');
}

export async function purgeStorageOrphans(reason: string) {
    return apiPost<StoragePurgeResult>('/api/admin/storage/orphans/purge', {
        confirm: true,
        reason
    });
}

// ------- Audio --------------------------------------

export function useAudioKpis() {
    return useQuery<AudioKpis>({
        queryKey: ['admin', 'audio', 'kpis'],
        queryFn: () => apiGet<AudioKpis>('/api/admin/audio/kpis'),
        refetchInterval: 30_000
    });
}

export function useElectronVersions() {
    return useQuery<ElectronVersionBucket[]>({
        queryKey: ['admin', 'audio', 'versions'],
        queryFn: () => apiGet<ElectronVersionBucket[]>('/api/admin/audio/versions'),
        refetchInterval: 60_000
    });
}

export function useAudioFormats() {
    return useQuery<AudioFormatRow[]>({
        queryKey: ['admin', 'audio', 'formats'],
        queryFn: () => apiGet<AudioFormatRow[]>('/api/admin/audio/formats'),
        refetchInterval: 60_000
    });
}

export function useAudioAccuracy() {
    return useQuery<AudioAccuracy>({
        queryKey: ['admin', 'audio', 'accuracy'],
        queryFn: () => apiGet<AudioAccuracy>('/api/admin/audio/accuracy'),
        refetchInterval: 60_000
    });
}

export function useAudioFailures() {
    return useQuery<AudioFailureRow[]>({
        queryKey: ['admin', 'audio', 'failures'],
        queryFn: () => apiGet<AudioFailureRow[]>('/api/admin/audio/failures'),
        refetchInterval: 30_000
    });
}

export function useAudioCampaigns() {
    return useQuery<AudioCampaign[]>({
        queryKey: ['admin', 'audio', 'campaigns'],
        queryFn: () => apiGet<AudioCampaign[]>('/api/admin/audio/campaigns'),
        refetchInterval: 60_000
    });
}

// ------- Notifications --------------------------------------

export function useNotificationKpis() {
    return useQuery<NotificationKpis>({
        queryKey: ['admin', 'notifications', 'kpis'],
        queryFn: () => apiGet<NotificationKpis>('/api/admin/notifications/kpis')
    });
}

export function useChannelDelivery() {
    return useQuery<ChannelDelivery[]>({
        queryKey: ['admin', 'notifications', 'channels'],
        queryFn: () => apiGet<ChannelDelivery[]>('/api/admin/notifications/channels')
    });
}

export function useTemplates() {
    return useQuery<TemplatePerformance[]>({
        queryKey: ['admin', 'notifications', 'templates'],
        queryFn: () => apiGet<TemplatePerformance[]>('/api/admin/notifications/templates')
    });
}

export async function importNotificationTemplateLegacyExport(payload?: {
    slugs?: string[];
    replace_published?: boolean;
    source?: 'bundled_export';
}) {
    return apiPost<TemplateImportResponse>(
        '/api/admin/notifications/templates/import-legacy-export',
        payload ?? {}
    );
}

export function useNotificationFailures() {
    return useQuery<NotificationFailure[]>({
        queryKey: ['admin', 'notifications', 'failures'],
        queryFn: () => apiGet<NotificationFailure[]>('/api/admin/notifications/failures')
    });
}

export function useBroadcasts() {
    return useQuery<BroadcastRow[]>({
        queryKey: ['admin', 'notifications', 'broadcasts'],
        queryFn: () => apiGet<BroadcastRow[]>('/api/admin/notifications/broadcasts')
    });
}

export async function pauseNotificationChannel(channel: string, reason: string) {
    return apiPost<{ success: true; channel: string; paused: true }>(
        `/api/admin/notifications/channels/${encodeURIComponent(channel)}/pause`,
        { reason }
    );
}

export async function resumeNotificationChannel(channel: string) {
    return apiPost<{ success: true; channel: string; paused: false }>(
        `/api/admin/notifications/channels/${encodeURIComponent(channel)}/resume`,
        {}
    );
}

export async function sendBroadcast(id: string) {
    return apiPost<{ success: true; broadcast: BroadcastRow }>(
        `/api/admin/notifications/broadcasts/${id}/send`,
        {}
    );
}

export async function cancelBroadcast(id: string) {
    return apiPost<{ success: true; broadcast: BroadcastRow }>(
        `/api/admin/notifications/broadcasts/${id}/cancel`,
        {}
    );
}

// ------- Restore & exports --------------------------------------

export function useRestoreKpis() {
    return useQuery<RestoreKpis>({
        queryKey: ['admin', 'restore', 'kpis'],
        queryFn: () => apiGet<RestoreKpis>('/api/admin/restore/kpis'),
        refetchInterval: 30_000
    });
}

export function useSoftDeletes() {
    return useQuery<SoftDeleteRow[]>({
        queryKey: ['admin', 'restore', 'soft-deleted'],
        queryFn: () => apiGet<SoftDeleteRow[]>('/api/admin/restore/soft-deleted'),
        refetchInterval: 30_000
    });
}

export function useRestoreTimeline() {
    return useQuery<RestoreTimelineRow[]>({
        queryKey: ['admin', 'restore', 'timeline'],
        queryFn: () => apiGet<RestoreTimelineRow[]>('/api/admin/restore/recent'),
        refetchInterval: 30_000
    });
}

export function useGdprExports() {
    return useQuery<GdprExportRow[]>({
        queryKey: ['admin', 'restore', 'exports'],
        queryFn: () => apiGet<GdprExportRow[]>('/api/admin/restore/exports'),
        refetchInterval: 30_000
    });
}

export function retryGdprExport(id: string) {
    return apiPost<GdprExportRow>(`/api/admin/restore/exports/${id}/retry`);
}

export function useRestorableActions(filters: AuditQueryFilters = {}) {
    return useQuery<RestorableAction[]>({
        queryKey: ['admin', 'restore', 'restorable-actions', filters],
        queryFn: async () =>
            normalizeRestorableActionsResponse(
                await apiGet<RestorableActionsApiResponse>(
                    `/api/restore/actions/restorable${buildAuditQueryString(filters)}`
                )
            ),
        refetchInterval: 30_000
    });
}

export function normalizeRestorableActionsResponse(
    payload: RestorableActionsApiResponse | null | undefined
): RestorableAction[] {
    const rows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.actions)
          ? payload.actions
          : Array.isArray(payload?.data)
            ? payload.data
            : [];

    return rows.map((row) => {
        const createdAt = getStringField(row, 'occurred_at') || getStringField(row, 'created_at');
        const stateBefore = getRecordField(row, 'state_before');
        const stateAfter = getRecordField(row, 'state_after');
        const summary = getStringField(row, 'summary');

        return {
            ...row,
            occurred_at: row.occurred_at ?? createdAt ?? null,
            ts: row.ts || formatRestoreTimestamp(createdAt),
            actor: row.actor || getStringField(row, 'user_id') || 'User',
            resource_label:
                row.resource_label ??
                summary ??
                getNamedSnapshotField(stateBefore) ??
                getNamedSnapshotField(stateAfter) ??
                null,
            state_before: stateBefore,
            state_after: stateAfter,
            can_redo: row.can_redo ?? row.is_undone
        };
    });
}

function getStringField(row: unknown, key: string): string | null {
    if (!row || typeof row !== 'object' || !(key in row)) return null;
    const value = (row as Record<string, unknown>)[key];
    return typeof value === 'string' && value.trim() ? value : null;
}

function getRecordField(row: unknown, key: string): Record<string, unknown> | null {
    if (!row || typeof row !== 'object' || !(key in row)) return null;
    const value = (row as Record<string, unknown>)[key];
    return value && typeof value === 'object' && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : null;
}

function getNamedSnapshotField(snapshot: Record<string, unknown> | null): string | null {
    if (!snapshot) return null;
    for (const key of ['song_name', 'display_name', 'name', 'title', 'email']) {
        const value = snapshot[key];
        if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return null;
}

function formatRestoreTimestamp(value: string | null): string {
    if (!value) return '—';
    const date = new Date(value);
    if (!Number.isFinite(date.getTime())) return value;
    return date.toISOString().replace('T', ' ').slice(0, 16);
}

export function undoRestorableAction(actionId: string, reason: string) {
    return apiPost<RestoreActionResult>(`/api/restore/actions/${actionId}/undo`, { reason });
}

export function redoRestorableAction(actionId: string, reason: string) {
    return apiPost<RestoreActionResult>(`/api/restore/actions/${actionId}/redo`, { reason });
}

// ------- Admin & audit --------------------------------------

export function useAdminAuditKpis() {
    return useQuery<AdminAuditKpis>({
        queryKey: ['admin', 'admin-audit', 'kpis'],
        queryFn: () => apiGet<AdminAuditKpis>('/api/admin/audit/kpis'),
        refetchInterval: 30_000
    });
}

export function useAdmins() {
    return useQuery<AdminUserRow[]>({
        queryKey: ['admin', 'admin-audit', 'admins'],
        queryFn: () => apiGet<AdminUserRow[]>('/api/admin/admins'),
        refetchInterval: 30_000
    });
}

export function useAdminAudit(filters: AuditQueryFilters = {}) {
    return useQuery<AdminAuditEntry[]>({
        queryKey: ['admin', 'admin-audit', 'audit', filters],
        queryFn: () =>
            apiGet<AdminAuditEntry[]>(`/api/admin/audit${buildAuditQueryString(filters)}`),
        refetchInterval: 30_000
    });
}

export function useResourceAuditHistory(resourceType: string | null, resourceId: string | null) {
    const filters: AuditQueryFilters = {
        source: 'all',
        resource_type: resourceType ?? undefined,
        resource_id: resourceId ?? undefined,
        limit: 100
    };

    return useQuery<AdminAuditEntry[]>({
        queryKey: ['admin', 'admin-audit', 'resource-history', resourceType, resourceId],
        queryFn: () =>
            apiGet<AdminAuditEntry[]>(`/api/admin/audit${buildAuditQueryString(filters)}`),
        enabled: !!resourceType && !!resourceId,
        refetchInterval: 30_000
    });
}

export function useImpersonations() {
    return useQuery<ImpersonationLogEntry[]>({
        queryKey: ['admin', 'admin-audit', 'impersonations'],
        queryFn: () => apiGet<ImpersonationLogEntry[]>('/api/admin/audit/impersonations'),
        refetchInterval: 30_000
    });
}

export async function addAdminUser(email: string, role: AdminRole) {
    return apiPost<AdminUserRow>('/api/admin/admins', { email, role });
}

export async function updateAdminUserRole(id: string, role: AdminRole) {
    return apiPatch<AdminUserRow>(`/api/admin/admins/${id}`, { role });
}

export async function removeAdminUser(id: string) {
    return apiDelete<{ success: boolean }>(`/api/admin/admins/${id}`);
}

export async function resetAdminUserTwoFactor(id: string) {
    return apiPost<{ success: boolean; removed: number }>(`/api/admin/admins/${id}/reset-2fa`);
}

export async function endAllImpersonations() {
    return apiPost<{ closed_count: number }>('/api/admin/audit/impersonations/end-all', {});
}

export async function downloadAdminAuditCsv(params: URLSearchParams) {
    return apiDownloadText(`/api/admin/audit/export.csv?${params.toString()}`, 'text/csv');
}

export async function downloadAnalyticsCsv(params: URLSearchParams) {
    return apiDownloadText(`/api/admin/analytics/export.csv?${params.toString()}`, 'text/csv');
}

// ------- Feature flag detail (appended 2026-04-19) --------------------------------------

import { FIXTURE_FLAG_OVERRIDES, FIXTURE_FLAG_HISTORY } from './fixtures';
import type { FlagOverride, FlagHistoryEntry } from './types';

export function useFlagOverrides(slug: string | null) {
    return useQuery<FlagOverride[]>({
        queryKey: ['admin', 'flags', 'overrides', slug],
        queryFn: () => simulate(FIXTURE_FLAG_OVERRIDES[slug!] ?? []),
        enabled: !!slug
    });
}

export function useFlagHistory(slug: string | null) {
    return useQuery<FlagHistoryEntry[]>({
        queryKey: ['admin', 'flags', 'history', slug],
        queryFn: () => simulate(FIXTURE_FLAG_HISTORY[slug!] ?? []),
        enabled: !!slug
    });
}

// ------- Analytics (extended) — per docs/specs/analytics.md §5.3, §6.2, §6.3 -------

import type { ConversionTimeBucket, PerFeatureFunnelSteps, PaidVsFreeRow } from './types';

export function useConversionTime() {
    return useQuery<{ buckets: ConversionTimeBucket[]; median_days: number }>({
        queryKey: ['admin', 'analytics', 'conversion-time'],
        queryFn: () =>
            apiGet<{ buckets: ConversionTimeBucket[]; median_days: number }>(
                '/api/admin/analytics/conversion-time'
            )
    });
}

export function usePerFeatureFunnels() {
    return useQuery<PerFeatureFunnelSteps[]>({
        queryKey: ['admin', 'analytics', 'per-feature-funnels'],
        queryFn: () => apiGet<PerFeatureFunnelSteps[]>('/api/admin/analytics/per-feature-funnels')
    });
}

export function usePaidVsFreeRetention() {
    return useQuery<PaidVsFreeRow[]>({
        queryKey: ['admin', 'analytics', 'paid-vs-free'],
        queryFn: () => apiGet<PaidVsFreeRow[]>('/api/admin/analytics/paid-vs-free')
    });
}

// ------- Restore / Jobs / Health (extended) — per specs §5.4, §6, §6.4, §8 -------

import type { SnapshotRow, SplitSheetStats, JobRun, HealthEvent } from './types';
import { FIXTURE_JOB_RUNS } from './fixtures';

export function useSnapshots() {
    return useQuery<SnapshotRow[]>({
        queryKey: ['admin', 'restore', 'snapshots'],
        queryFn: () => apiGet<SnapshotRow[]>('/api/admin/restore/snapshots'),
        refetchInterval: 60_000
    });
}

export function useSplitSheetStats() {
    return useQuery<SplitSheetStats>({
        queryKey: ['admin', 'restore', 'split-sheet-stats'],
        queryFn: () => apiGet<SplitSheetStats>('/api/admin/restore/split-sheets'),
        refetchInterval: 60_000
    });
}

export function useJobRuns(jobName: string | null) {
    return useQuery<JobRun[]>({
        queryKey: ['admin', 'jobs', 'runs', jobName],
        queryFn: () => simulate(jobName ? (FIXTURE_JOB_RUNS[jobName] ?? []) : []),
        enabled: !!jobName
    });
}

export function useHealthEvents() {
    return useQuery<HealthEvent[]>({
        queryKey: ['admin', 'health', 'events'],
        queryFn: () => apiGet<HealthEvent[]>('/api/admin/health/events'),
        refetchInterval: 30_000
    });
}

// ------- Licensing — payout-run preview (appended 2026-04-19) -------
// Per docs/specs/licensing.md §7 — surfaces what the weekly cron would do.

import type { PayoutRunPreview } from './types';
import { FIXTURE_PAYOUT_RUN_PREVIEW } from './fixtures';

export function usePayoutPreview() {
    return useQuery<PayoutRunPreview>({
        queryKey: ['admin', 'licensing', 'payout-preview'],
        queryFn: () => simulate(FIXTURE_PAYOUT_RUN_PREVIEW)
    });
}

// ------- Import prompt history — per docs/specs/import-pipeline.md §7 -------

import type { ImportPromptVersion, TaxonomyMigration } from './types';
import { FIXTURE_IMPORT_PROMPT_HISTORY } from './fixtures';

export function useImportPromptHistory(
    slug: 'review' | 'pattern-discovery' | 'display-name-cleaner' | null
) {
    return useQuery<ImportPromptVersion[]>({
        queryKey: ['admin', 'imports', 'prompt-history', slug],
        queryFn: () => simulate(FIXTURE_IMPORT_PROMPT_HISTORY[slug!] ?? []),
        enabled: !!slug
    });
}

// ------- Taxonomy migrations — per docs/specs/taxonomy.md §7 -------

export function useTaxonomyMigrations() {
    return useQuery<TaxonomyMigration[]>({
        queryKey: ['admin', 'taxonomy', 'migrations'],
        queryFn: () => apiGet<TaxonomyMigration[]>('/api/admin/taxonomy/migrations')
    });
}
